import { useEffect, useRef, useState } from "react";

/** Grains scattered per pointer sample. */
const GRAINS_PER_SAMPLE = 130;
/** Radius the scatter spreads over, in CSS pixels. */
const SCATTER_RADIUS = 17;
/**
 * How strongly each frame pulls the surface back toward the base. Deliberately
 * low: an etch-a-sketch holds its drawing, so the trail should linger for
 * seconds rather than evaporate in a few frames.
 */
const SETTLE_ALPHA = 0.012;
/** Frames the loop keeps settling after the pointer leaves (~7s at 60fps). */
const SETTLE_FRAMES = 430;
/** Specks in the tiling sand texture. */
const TEXTURE_SPECKS = 2600;
const TEXTURE_TILE = 96;

const RAMP_TOKENS = ["--logo-ramp-4", "--logo-ramp-3", "--logo-ramp-2", "--logo-ramp-1"] as const;
const RAMP_FALLBACK = ["#d2c2ac", "#cbac86", "#d8975a", "#ff7612"] as const;

/**
 * Placeholder panel for the image that will eventually live here.
 *
 * The surface is a canvas: the logo ramp under a tiling sand texture. Moving a
 * pointer across it scatters grains along the path, like drawing in sand, and
 * the surface settles back toward its base a little each frame so the drawing
 * fades rather than accumulating forever.
 *
 * Deliberately NOT a CSS gradient tracking the cursor — that reads as a moving
 * highlight, not as displaced material. And the panel's geometry is fixed: it
 * responds to the pointer only in what is painted inside it, never by changing
 * its own size or corners, so nothing reflows under the cursor.
 *
 * Motion is progressive enhancement: the base surface paints once without any
 * animation, `prefers-reduced-motion` skips the scatter loop entirely, the rAF
 * loop runs only while there is something to settle, and an IntersectionObserver
 * stops it while the panel is off screen.
 */
export function BreathingPanel() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const root = document.documentElement;
    const readRamp = (): readonly string[] => {
      const styles = window.getComputedStyle(root);
      return RAMP_TOKENS.map((token, index) => {
        const value = styles.getPropertyValue(token).trim();
        return value.startsWith("#") || value.startsWith("rgb")
          ? value
          : (RAMP_FALLBACK[index] ?? "#d8975a");
      });
    };

    /** Tiling speck texture — the "sand". Built once, reused as a pattern. */
    const buildTexture = (): HTMLCanvasElement => {
      const tile = document.createElement("canvas");
      tile.width = TEXTURE_TILE;
      tile.height = TEXTURE_TILE;
      const tileContext = tile.getContext("2d");
      if (!tileContext) return tile;
      for (let i = 0; i < TEXTURE_SPECKS; i += 1) {
        const light = Math.random() > 0.45;
        const alpha = 0.03 + Math.random() * 0.07;
        tileContext.fillStyle = light
          ? `rgba(255, 255, 255, ${String(alpha)})`
          : `rgba(38, 37, 34, ${String(alpha * 0.8)})`;
        tileContext.fillRect(
          Math.random() * TEXTURE_TILE,
          Math.random() * TEXTURE_TILE,
          Math.random() < 0.15 ? 1.6 : 1,
          Math.random() < 0.15 ? 1.6 : 1,
        );
      }
      return tile;
    };

    const texture = buildTexture();
    const base = document.createElement("canvas");
    let width = 0;
    let height = 0;
    let ratio = 1;

    const paintBase = (): void => {
      const baseContext = base.getContext("2d");
      if (!baseContext || width === 0 || height === 0) return;

      baseContext.setTransform(ratio, 0, 0, ratio, 0, 0);
      const ramp = readRamp();
      // Matches the 118deg of the CSS the canvas replaced.
      const gradient = baseContext.createLinearGradient(0, height, width, 0);
      ramp.forEach((colour, index) => {
        gradient.addColorStop(index / (ramp.length - 1), colour);
      });
      baseContext.fillStyle = gradient;
      baseContext.fillRect(0, 0, width, height);

      const pattern = baseContext.createPattern(texture, "repeat");
      if (pattern) {
        baseContext.fillStyle = pattern;
        baseContext.fillRect(0, 0, width, height);
      }
    };

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      ratio = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      for (const surface of [canvas, base]) {
        surface.width = Math.round(width * ratio);
        surface.height = Math.round(height * ratio);
      }
      paintBase();
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.drawImage(base, 0, 0, width, height);
    };

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const queue: { x: number; y: number }[] = [];
    let settling = 0;
    let frame = 0;

    const scatter = (x: number, y: number): void => {
      for (let i = 0; i < GRAINS_PER_SAMPLE; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        // sqrt keeps the scatter even across the disc instead of clumping centrally.
        const distance = Math.sqrt(Math.random()) * SCATTER_RADIUS;
        /*
         * Both light and dark grains: highlights alone read as a glow, whereas
         * mixing in shadow specks gives the surface tooth and makes it read as
         * displaced material.
         */
        const light = Math.random() > 0.34;
        context.fillStyle = light
          ? `rgba(255, 253, 248, ${String(0.42 + Math.random() * 0.5)})`
          : `rgba(46, 40, 33, ${String(0.16 + Math.random() * 0.3)})`;
        const size = 0.7 + Math.random() * 1.5;
        context.fillRect(
          x + Math.cos(angle) * distance,
          y + Math.sin(angle) * distance,
          size,
          size,
        );
      }
    };

    const step = (): void => {
      // Settle: pull the whole surface a little back toward the base.
      context.globalAlpha = SETTLE_ALPHA;
      context.drawImage(base, 0, 0, width, height);
      context.globalAlpha = 1;

      while (queue.length > 0) {
        const point = queue.shift();
        if (point) scatter(point.x, point.y);
      }

      settling -= 1;
      frame = settling > 0 ? window.requestAnimationFrame(step) : 0;
    };

    const wake = (): void => {
      settling = SETTLE_FRAMES;
      if (frame === 0) frame = window.requestAnimationFrame(step);
    };

    const onMove = (event: PointerEvent): void => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (reduced) {
        scatter(x, y);
        return;
      }
      queue.push({ x, y });
      wake();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setInView(entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(canvas);

    const resizeObserver = new ResizeObserver(() => {
      resize();
    });
    resizeObserver.observe(canvas);

    resize();
    canvas.addEventListener("pointermove", onMove);

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
      canvas.removeEventListener("pointermove", onMove);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div aria-hidden="true" className="breathing-panel" data-breathing={inView}>
      <canvas className="breathing-panel__surface" ref={canvasRef} />
    </div>
  );
}
